import { type TypographyVariantsOptions } from '@mui/material/styles';

import { fontFamily } from './tokens';

/** 1.25 scale: 12 / 14 / 16 / 20 / 25 / 31 / 39 / 49 / 61 px. */
const display = {
  fontFamily: fontFamily.display,
  fontWeight: 800,
  letterSpacing: '-0.02em',
  lineHeight: 1.08,
  textWrap: 'balance',
} as const;

/**
 * The three fluid display sizes, named so a component can ask for one back.
 *
 * They were literals inside the variants below, which is where a type scale
 * belongs right up until something needs to *restate* one. `SectionHeading`
 * does: it sets a flat 20px on a phone and the scale's own clamp from `md`
 * up, and a responsive `fontSize` has to carry a real value at both ends —
 * MUI writes `min-width: 0` for an `xs` key, so leaving `md` out would apply
 * the phone size at every width instead of only below the breakpoint.
 *
 * Exported rather than copied, so the heading that steps out of the scale on
 * a phone cannot drift from the scale it steps back into.
 */
export const displaySize = {
  h1: 'clamp(2.44rem, 5vw + 1rem, 3.81rem)',
  h2: 'clamp(1.95rem, 3vw + 1rem, 3.06rem)',
  h3: 'clamp(1.56rem, 2vw + 0.75rem, 2.44rem)',
} as const;

export const typography: TypographyVariantsOptions = {
  fontFamily: fontFamily.body,
  htmlFontSize: 16,
  fontSize: 16,
  h1: { ...display, fontSize: displaySize.h1 },
  h2: { ...display, fontWeight: 700, fontSize: displaySize.h2 },
  h3: { ...display, fontWeight: 700, fontSize: displaySize.h3 },
  h4: { ...display, fontWeight: 700, fontSize: '1.56rem', lineHeight: 1.2 },
  h5: {
    ...display,
    fontWeight: 700,
    fontSize: '1.25rem',
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
  },
  h6: { ...display, fontWeight: 600, fontSize: '1rem', lineHeight: 1.3, letterSpacing: 0 },
  subtitle1: { fontSize: '1.25rem', lineHeight: 1.5 },
  subtitle2: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
  body1: { fontSize: '1rem', lineHeight: 1.6 },
  body2: { fontSize: '0.875rem', lineHeight: 1.55 },
  caption: { fontSize: '0.75rem', lineHeight: 1.45 },
  overline: {
    fontFamily: fontFamily.display,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    lineHeight: 1.4,
  },
  button: {
    fontFamily: fontFamily.display,
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'none',
  },
};
